import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { useOpenApiRequest, OpenApiRequestStatus } from "typescript-request-utils";
import { CustomerWithUserCustomers } from "../../../../api/models/CustomerWithUserCustomers";
import { Organization } from "../../../../api/models/Organization";
import { organizationTableColumns } from "../../../configuration/tableColumns";
import { getApiRegistry } from "../../../state/apiRegistry";
import { organizationsBothTabs } from "../../table/nestedTableTabOptions";
import { MobileItemClosedIcon, MobileItemOpenedIcon } from "../../table/rowIcons";
import { Column, ColumnItem, ColumnItemLinksTo, HeaderColumn, ItemHeaderWithTabs, ItemWrapper } from "../mobileComponents";
import moment from "moment";
import { UserCustomer } from "../../../../api/models/UserCustomer";
import { useTheme } from "../../../../features/context/CustomerContext";
import useCustomTranslation from "../../../../features/hooks/CustomUseTranslation";

interface DivProps {
    theme: any;
    customerTheme: any;
}

export const NestedTabButton = styled.div<DivProps>`
    border-radius: 100px;
    border: 2px solid ${(props) => props.customerTheme.brandColor1};
    width: auto;
    padding: 5px 10px;
    cursor: pointer;
    background-color: ${props => props.theme.active ? props.customerTheme.brandColor1 : "white"};
    color: ${props => props.theme.active ? "white" : props.customerTheme.brandColor1};
`
export const OrganizationItemWrapper = styled(ItemWrapper)`
    padding-left: 25px;
    border-bottom: none;
`
export const OrganizationCustomersWrapper = styled(ItemWrapper)`
    padding-left: 25px;
    padding-top: 10px;
`
export const ColumnItemBold = styled(ColumnItem)`
    font-weight: bold;
`
export const HeaderRow = styled.div`
    display: flex;
    flex-direction: row;
`

export interface OrganizationMobileItemProps {
    organization: Organization;
    reloadOrganizations: () => void;
}

const OrganizationMobileItem: FC<OrganizationMobileItemProps> = (props) => {
    const [showFullItem, setShowFullItem] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('customerNumbers');
    const [customers, setCustomers] = useState<CustomerWithUserCustomers[]>();
    const [linkedUserCustomers, setUserCustomers] = useState<UserCustomer[]>([]);

    const [organizationCustomerRequest, loadOrganizationCustomers] = useOpenApiRequest(getApiRegistry().organizationsApi.getOrganizationCustomers);
    const [foundUserCustomers, setFoundUserCustomers] = useState<boolean>(false);

    const { t } = useCustomTranslation();
    const theme = useTheme();

    var activeLinkings: UserCustomer[] = [];

    useEffect(() => {
        loadOrganizationCustomers(props.organization.name);
        // eslint-disable-next-line
    }, [loadOrganizationCustomers]);

    useEffect(() => {
        if (organizationCustomerRequest.type === OpenApiRequestStatus.Complete) {
            setCustomers(organizationCustomerRequest.responsePayload);
        }
        // eslint-disable-next-line
    }, [organizationCustomerRequest]);

   useEffect(() => {
        if(customers){
            for (let customer of customers) {
                if(customer.userCustomers.length > 0){
                    setFoundUserCustomers(true);
                    collectActiveUserCustomers(customer);
                }
            }
        }
        setUserCustomers(activeLinkings);
        // eslint-disable-next-line
    }, [showFullItem]);

    var currentDate = new Date();
    var today = moment(currentDate).format('yyyy-MM-DDTHH:mm:ss+SSSS');

    const collectActiveUserCustomers = (customer: CustomerWithUserCustomers) => {
        const uniqueUsers = new Set();
        customer.userCustomers.forEach((userCustomer) => {
            if (userCustomer.startDate !== null && userCustomer.endDate !== null) {
                var startDate = moment(userCustomer.startDate).format('yyyy-MM-DDTHH:mm:ss+SSSS')
                var endDate = moment(userCustomer.endDate).format('yyyy-MM-DDTHH:mm:ss+SSSS')
                if ((userCustomer.startDate === undefined || startDate < today) && (userCustomer.endDate === undefined || endDate > today) && !uniqueUsers.has(userCustomer.user.username)) {
                    activeLinkings.push(userCustomer)
                    uniqueUsers.add(userCustomer.user.username);             
                }
            }
        });
    }

    return (
        <div>
            {!showFullItem && 
                <ItemWrapper>
                    <Column>
                        <MobileItemClosedIcon onClick={() => setShowFullItem(true)}/>
                    </Column>            
                    <HeaderColumn>
                        <ColumnItem>{ t(organizationTableColumns[0].columnName) }</ColumnItem>
                    </HeaderColumn>
                    <Column>
                        <ColumnItem>{ props.organization.name}</ColumnItem>
                    </Column>
                </ItemWrapper>
            }

            {showFullItem && <>
                <ItemHeaderWithTabs>
                    <Column>
                        <MobileItemOpenedIcon onClick={() => setShowFullItem(false)}/>
                    </Column>
                    <Column>
                        <NestedTabButton customerTheme={theme} theme={{active: 'customerNumbers' === activeTab}} onClick={() => setActiveTab('customerNumbers')}>{t(organizationsBothTabs[0].tabLabel)}</NestedTabButton>
                    </Column> 
                    {foundUserCustomers && 
                        <Column>
                            <NestedTabButton customerTheme={theme} theme={{active: 'users' === activeTab}} onClick={() => setActiveTab('users')}>{t(organizationsBothTabs[1].tabLabel)}</NestedTabButton>
                        </Column>} 
                </ItemHeaderWithTabs>

                <OrganizationItemWrapper>                
                    <HeaderColumn>
                        {organizationTableColumns.map((row, index) => 
                            <ColumnItem key={index}>{t(row.columnName)}</ColumnItem>)}            
                    </HeaderColumn>
                    <Column>
                        <ColumnItem>{props.organization.name}</ColumnItem>
                        <ColumnItem>{props.organization.description}</ColumnItem>
                    </Column>
                </OrganizationItemWrapper>

                <OrganizationCustomersWrapper>
                    {activeTab === 'customerNumbers' && customers && 
                        <Column>
                            <ColumnItemBold>{t('Customer number')}</ColumnItemBold>
                            {customers.map(customer => (
                                <ColumnItem key={customer.id}>{customer.customerNumber}</ColumnItem>
                            ))}
                        </Column>}
                    {activeTab === 'users' && linkedUserCustomers  && <>
                        <Column>
                            <ColumnItemBold>{t('Username')}</ColumnItemBold>
                            {linkedUserCustomers.map((userCustomer, index) => (
                                <ColumnItemLinksTo key={index}>{userCustomer.user.username}</ColumnItemLinksTo>
                            ))}
                        </Column>
                        <Column>
                            <ColumnItemBold />
                            {linkedUserCustomers.map((userCustomer, index) => (
                                <ColumnItemLinksTo key={index}>{t("Links to ")} {userCustomer.customer?.customerNumber}</ColumnItemLinksTo>
                            ))}
                        </Column></>}
                </OrganizationCustomersWrapper></>}
        </div>
    )
}

export default OrganizationMobileItem;
